// hooks/useExamData.js
import { useEffect, useRef, useCallback } from "react";
import { downloadAudioToIndexDB } from "../utils/audioUtils";
import { hasMP3 } from "../utils/indexedDB";
import { useExam } from "../context/ExamContext";
import { useGetUserAnswers } from "../services";
import { buildSectionAnswersKey, buildSectionQuestionsKey, buildAudioKey, DEMO_AUDIO_KEY } from "../constants/storageKeys";

export function useInitializeExamResources(
  examData,
  activeView,
  setLoadingListeningAudio,
  isDemoExam
) {
  const {
    isRealExam,
    setReadingAnswers,
    setListeningAnswers,
    setWritingAnswers,
    uprId,
    productType,
  } = useExam(null);

  const hasInitializedRef = useRef(false);

  // Bug Fix #3: Expose reset function to allow re-initialization after takeover
  const resetInitialization = useCallback(() => {
    hasInitializedRef.current = false;
  }, []);

  // This hook MUST be called unconditionally (Rules of Hooks)
  const {
    triggerGetUserAnswers,
    userAnswers,
    errorUserAnswers,
  } = useGetUserAnswers(activeView, uprId);

  // Fetch user answers
  // =====================================================
  useEffect(() => {
    // Guard moved INSIDE effect (not before hooks)
    if (hasInitializedRef.current) return;
    if (!examData) return;
    if (!activeView) return;
    if (!isRealExam) return;
    if (!uprId || !productType) return;

    const answersKey = buildSectionAnswersKey(activeView);
    const exists = localStorage.getItem(answersKey) !== null;

    if (!exists) {
      triggerGetUserAnswers();
    }

    hasInitializedRef.current = true;
  }, [
    examData,
    activeView,
    isRealExam,
    uprId,
    productType,
    triggerGetUserAnswers,
  ]);

  // Persist answers 
  // =====================================================
  useEffect(() => {
    if (!userAnswers || errorUserAnswers != null) return;

    const persistKey = buildSectionAnswersKey(activeView);
    localStorage.setItem(persistKey, JSON.stringify(userAnswers));

    if (activeView === "reading") setReadingAnswers(userAnswers);
    if (activeView === "listening") setListeningAnswers(userAnswers);
    if (activeView === "writing") setWritingAnswers(userAnswers);
  }, [
    userAnswers,
    errorUserAnswers,
    activeView,
    setReadingAnswers,
    setListeningAnswers,
    setWritingAnswers,
  ]);

  //Exam data + audio init
  // =====================================================
  useEffect(() => {
    if (!examData) return;
    if (!activeView) return;

    (async () => {
      // Store questions
      ["reading", "writing", "listening"].forEach((section) => {
        const key = buildSectionQuestionsKey(section);
        const dataKey = `${section}Questions`;
        const value = examData[dataKey];

        if (value != null) {
          const storage = isRealExam ? localStorage : sessionStorage;
          storage.setItem(key, JSON.stringify(value));
        }
      });

      // Handle listening audio
      if (activeView === "listeningIntro" || activeView === "listening") {
        const audioKey = !isDemoExam
          ? buildAudioKey(uprId)
          : DEMO_AUDIO_KEY;

        const hasListeningAudio = await hasMP3(audioKey);

        if (!hasListeningAudio) {
          await downloadAudioToIndexDB(
            setLoadingListeningAudio,
            isDemoExam,
            uprId
          );
        }
      }

      setLoadingListeningAudio(false);
    })();
  }, [
    examData,
    activeView,
    isRealExam,
    isDemoExam,
    uprId,
    setLoadingListeningAudio,
  ]);

  // Return reset function for Bug Fix #3
  return { resetInitialization };
}
